import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { Page } from "@digital-services/shell";
import { DataError, LoadingIndicator } from "@digital-services/ui";
import { PaginationContextProvider, shortenOrgName } from "@ds-cps/ui";

import {
  BreadCrumbItem,
  CustomBreadCrumb
} from "@components/BreadCrumb/CustomBreadCrumb";

import { OrganizationContextProvider } from "@organization/context/OrganizationContextProvider";
import { useGetOrganization } from "@organization/services/use-get-organization";

import OrganizationInfo from "../components/OrganizationInfo";
import OrganizationTabMenu from "./OrganizationTabMenu";

const OrganizationDetailPage: FunctionComponent = () => {
  const { organizationId } = useParams();
  const { t } = useTranslation(undefined, { keyPrefix: "organization" });
  const { t: tCommon } = useTranslation();

  const { organization, isLoading, error } = useGetOrganization(
    organizationId ?? ""
  );

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  // IMPORTANT here, that <OrganizationContextProvider> MUST ONLY mount when organization data is ready
  if (!organization) return null;
  // Guards pass → Provider mounts with real data as initial state
  // then Children render → context already has data

  const shortOrgName = shortenOrgName(organization?.name);

  const breadCrumbs: BreadCrumbItem[] = [
    {
      name: tCommon("home.nav_item.label"),
      url: "/"
    },
    {
      name: tCommon("organizations.nav_item.label"),
      url: "/organizations"
    },
    {
      name: shortOrgName!
    }
  ];

  return (
    <Page title={`${t("page_title")} - ${shortOrgName}`}>
      <div data-testid="organization-details-container">
        <CustomBreadCrumb breadCrumbs={breadCrumbs} />
        <div
          className="flex flex-col pt-5"
          data-testid="organization-details-content"
        >
          <OrganizationContextProvider initialOrganization={organization}>
            <OrganizationInfo />
            <PaginationContextProvider>
              <OrganizationTabMenu />
            </PaginationContextProvider>
          </OrganizationContextProvider>
        </div>
      </div>
    </Page>
  );
};

export default OrganizationDetailPage;
